import { useEffect, useRef, useState, ComponentType } from "react";

/**
 * Hook partagé : charge framer-motion UNIQUEMENT après la première
 * interaction utilisateur (scroll, mousemove, touchstart, keydown, click).
 *
 * Pourquoi pas requestIdleCallback ? Parce que sur desktop rapide,
 * "idle" arrive trop tôt et PageSpeed voit motion-*.js dans le critical
 * path LCP (mesuré : +3 240 ms de latence chemin critique).
 *
 * Avec ce pattern :
 *   - 0 ms LCP : motion n'est PAS dans la chaîne critique
 *   - L'utilisateur déclenche le chargement par sa première action
 *   - Fallback 4 s pour les visiteurs totalement immobiles
 *
 * Cache module-level partagé entre tous les composants : 1 seul fetch réseau.
 */

type Loader = () => Promise<ComponentType<any>>;

let cached: Map<string, ComponentType<any>> = new Map();
let pendings: Map<string, Promise<ComponentType<any>>> = new Map();
let interactionPromise: Promise<void> | null = null;

const waitForInteraction = (): Promise<void> => {
  if (interactionPromise) return interactionPromise;
  interactionPromise = new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }
    const events = ["pointerdown", "touchstart", "keydown", "scroll", "mousemove"];
    let timeoutId: number | undefined;
    const cleanup = () => {
      events.forEach((ev) => window.removeEventListener(ev, onAny));
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
    const onAny = () => {
      cleanup();
      resolve();
    };
    events.forEach((ev) =>
      window.addEventListener(ev, onAny, { once: true, passive: true } as AddEventListenerOptions),
    );
    // Fallback : si l'utilisateur ne fait rien pendant 4 s, on charge quand même
    timeoutId = window.setTimeout(() => {
      cleanup();
      resolve();
    }, 4000);
  });
  return interactionPromise;
};

export const useMotionOnInteraction = (
  key: string,
  loader: Loader,
  options?: { freezeOnMount?: boolean },
): ComponentType<any> | null => {
  const [Comp, setComp] = useState<ComponentType<any> | null>(cached.get(key) ?? null);
  // freezeOnMount : si le composant n'était pas encore chargé au montage, on ne
  // le monte JAMAIS en cours de route — le swap Fragment → motion remonterait
  // tout le sous-arbre et réinitialiserait l'état local de la page (onglets,
  // formulaires…). La navigation suivante récupérera le composant depuis le
  // cache et animera normalement la page fraîchement montée.
  const frozenRef = useRef(options?.freezeOnMount === true && Comp === null);

  useEffect(() => {
    if (Comp || frozenRef.current) return;
    let cancelled = false;
    waitForInteraction().then(() => {
      if (cancelled || frozenRef.current) return;
      const existing = cached.get(key);
      if (existing) {
        setComp(() => existing);
        return;
      }
      let pending = pendings.get(key);
      if (!pending) {
        pending = loader().then((c) => {
          cached.set(key, c);
          return c;
        });
        pendings.set(key, pending);
      }
      pending.then((c) => {
        if (!cancelled && !frozenRef.current) setComp(() => c);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [Comp, key, loader]);

  return Comp;
};
